import { useEffect, useState, useRef } from "react";
import { api } from "../utils/api";
import { usePraxis } from "../context/PraxisContext";

/**
 * Admin-Tab für die „Über mich"-Sektion der Startseite.
 * Editiert alle Textfelder + Bilder (Portrait, Hero-Portrait) + drei Listen
 * (Karriere-Timeline, Auszeichnungen, Mitgliedschaften-Tags).
 */
export default function AboutManager() {
  const { about, refresh } = usePraxis();
  const [values, setValues] = useState({
    label: "", name_pre: "", name_em: "",
    intro: "", photo: "", hero_photo: "",
    badge_number: "", badge_text: "",
    career_heading: "", career: [],
    awards_heading: "", awards: [],
    memberships_heading: "", memberships: [],
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (about) setValues({ ...values, ...about });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [about]);

  const set = (patch) => setValues({ ...values, ...patch });

  const save = async () => {
    setError(""); setMessage("");
    setSaving(true);
    const payload = {
      about_label: values.label,
      about_name_pre: values.name_pre,
      about_name_em: values.name_em,
      about_intro: values.intro,
      about_photo: values.photo,
      about_hero_photo: values.hero_photo,
      about_badge_number: values.badge_number,
      about_badge_text: values.badge_text,
      about_career_heading: values.career_heading,
      about_career: JSON.stringify(values.career || []),
      about_awards_heading: values.awards_heading,
      about_awards: JSON.stringify(values.awards || []),
      about_memberships_heading: values.memberships_heading,
      about_memberships: JSON.stringify(values.memberships || []),
    };
    const { data } = await api.updateSettings(payload);
    setSaving(false);
    if (data && data.ok) {
      setMessage('„Über mich"-Inhalte gespeichert.');
      refresh();
      setTimeout(() => setMessage(""), 4000);
    } else {
      setError("Speichern fehlgeschlagen.");
    }
  };

  return (
    <div data-testid="about-manager">
      <div style={{ marginBottom: "1rem" }}>
        <h2 className="admin-list-title" style={{ margin: 0 }}>„Über mich&#8220;-Bereich verwalten</h2>
        <p className="admin-align-hint" style={{ margin: "0.3rem 0 0" }}>
          Diese Inhalte erscheinen auf der Startseite im Abschnitt „Über mich&#8220;.
        </p>
      </div>

      {message && <p className="admin-success" data-testid="about-message">{message}</p>}
      {error && <p className="admin-error">{error}</p>}

      {/* BILDER */}
      <details className="admin-collapse" open>
        <summary>1. Bilder</summary>
        <div className="admin-collapse-body">
          <PhotoUpload
            label="Freigestelltes Portrait – auf der Startseite (Hero, ganz oben)"
            value={values.hero_photo}
            onChange={(url) => set({ hero_photo: url })}
            testid="about-hero-photo"
            transparentAllowed
          />
          <PhotoUpload
            label='Portrait im „Über mich"-Bereich'
            value={values.photo}
            onChange={(url) => set({ photo: url })}
            testid="about-photo"
          />
          <div className="admin-two-col" style={{ marginTop: "1.5rem" }}>
            <label className="admin-label">
              Erfahrungs-Badge Zahl (z. B. „20+&#8220;)
              <input type="text" value={values.badge_number}
                     onChange={(e) => set({ badge_number: e.target.value })}
                     maxLength={10} data-testid="about-badge-number"/>
            </label>
            <label className="admin-label">
              Erfahrungs-Badge Text
              <input type="text" value={values.badge_text}
                     onChange={(e) => set({ badge_text: e.target.value })}
                     maxLength={40} data-testid="about-badge-text"/>
            </label>
          </div>
        </div>
      </details>

      {/* TITEL + INTRO */}
      <details className="admin-collapse" open>
        <summary>2. Überschrift + Intro-Text</summary>
        <div className="admin-collapse-body">
          <div className="admin-two-col">
            <label className="admin-label">
              Kleiner Text über dem Namen
              <input type="text" value={values.label}
                     onChange={(e) => set({ label: e.target.value })}
                     maxLength={40} data-testid="about-label"/>
            </label>
            <div/>
          </div>
          <div className="admin-two-col">
            <label className="admin-label">
              Name Präfix (z. B. „Dr. med.&#8220;)
              <input type="text" value={values.name_pre}
                     onChange={(e) => set({ name_pre: e.target.value })}
                     maxLength={40} data-testid="about-name-pre"/>
            </label>
            <label className="admin-label">
              Name (kursiv angezeigt)
              <input type="text" value={values.name_em}
                     onChange={(e) => set({ name_em: e.target.value })}
                     maxLength={80} data-testid="about-name-em"/>
            </label>
          </div>
          <label className="admin-label">
            Intro-Paragraph
            <textarea value={values.intro} rows={4}
                      onChange={(e) => set({ intro: e.target.value })}
                      maxLength={800} data-testid="about-intro"/>
          </label>
        </div>
      </details>

      {/* KARRIERE */}
      <details className="admin-collapse">
        <summary>3. Beruflicher Werdegang</summary>
        <div className="admin-collapse-body">
          <label className="admin-label">
            Überschrift
            <input type="text" value={values.career_heading}
                   onChange={(e) => set({ career_heading: e.target.value })}
                   maxLength={80} data-testid="about-career-heading"/>
          </label>
          <ListEditor
            items={values.career}
            onChange={(items) => set({ career: items })}
            fields={[
              { key: "years", label: "Zeitraum", placeholder: "z. B. 2001 – 2010" },
              { key: "title", label: "Titel (fett)", placeholder: "z. B. Facharztausbildung" },
              { key: "text",  label: "Beschreibung", placeholder: "Details", multiline: true },
            ]}
            addLabel="+ Neuer Karriere-Eintrag"
            testidBase="about-career"
          />
        </div>
      </details>

      {/* AUSZEICHNUNGEN */}
      <details className="admin-collapse">
        <summary>4. Auszeichnungen</summary>
        <div className="admin-collapse-body">
          <label className="admin-label">
            Überschrift
            <input type="text" value={values.awards_heading}
                   onChange={(e) => set({ awards_heading: e.target.value })}
                   maxLength={80} data-testid="about-awards-heading"/>
          </label>
          <ListEditor
            items={values.awards}
            onChange={(items) => set({ awards: items })}
            fields={[
              { key: "year", label: "Jahr", placeholder: "z. B. 2007" },
              { key: "text", label: "Auszeichnung", placeholder: "Beschreibung", multiline: true },
            ]}
            addLabel="+ Neue Auszeichnung"
            testidBase="about-awards"
          />
        </div>
      </details>

      {/* MITGLIEDSCHAFTEN */}
      <details className="admin-collapse">
        <summary>5. Mitgliedschaften (Tags)</summary>
        <div className="admin-collapse-body">
          <label className="admin-label">
            Überschrift
            <input type="text" value={values.memberships_heading}
                   onChange={(e) => set({ memberships_heading: e.target.value })}
                   maxLength={80} data-testid="about-memberships-heading"/>
          </label>
          <TagListEditor
            items={values.memberships}
            onChange={(items) => set({ memberships: items })}
            addPlaceholder="Mitgliedschaft/Verband hinzufügen…"
            testidBase="about-membership"
          />
        </div>
      </details>

      <div className="admin-actions-row" style={{ marginTop: "2rem" }}>
        <button type="button" onClick={save} className="admin-btn admin-btn-primary" disabled={saving}
                data-testid="about-save">
          {saving ? "Speichere…" : 'Alle „Über mich"-Inhalte speichern'}
        </button>
      </div>
    </div>
  );
}

/* -------- Photo Upload mit Vorschau -------- */
/**
 * Konvertiert File → JPEG data URL via Canvas.
 * Wir konvertieren immer, weil das Server-GD kein libpng hat und PNGs sonst scheitern.
 * Auf 1600px begrenzt, damit die Payload nicht zu groß wird.
 */
function convertToJpeg(file, maxSide = 1600, quality = 0.92) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        // Weißer Hintergrund für PNGs mit Transparenz
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

function PhotoUpload({ label, value, onChange, testid, transparentAllowed }) {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const inputRef = useRef();

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      setErr("Bild zu groß (max. 8 MB).");
      return;
    }
    setErr("");
    setUploading(true);
    try {
      // Client-seitig zu JPEG konvertieren (Server hat kein libpng für PNG).
      const jpegDataUrl = await convertToJpeg(file);
      const { data } = await api.uploadAboutPhoto(jpegDataUrl);
      if (data && data.ok && data.url) {
        onChange(data.url);
      } else {
        setErr((data && data.message) || "Upload fehlgeschlagen.");
      }
    } catch {
      setErr("Upload fehlgeschlagen.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="photo-upload-block" data-testid={`${testid}-wrapper`}>
      <h4 className="photo-upload-label">{label}</h4>
      <div style={{ display: "flex", gap: "1rem", alignItems: "flex-start", marginTop: "0.4rem" }}>
        <div style={{
          width: "140px", height: "140px",
          borderRadius: transparentAllowed ? "12px" : "50%",
          overflow: "hidden", background: "#FBF8F3",
          border: "1px solid #E6DFD4", flexShrink: 0,
        }}>
          {value ? (
            <img src={value} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }}
                 onError={(e) => { e.currentTarget.style.opacity = 0.3; }}/>
          ) : (
            <div style={{ width: "100%", height: "100%", display: "flex",
                          alignItems: "center", justifyContent: "center",
                          color: "#B8956A", fontSize: "0.85rem" }}>Kein Bild</div>
          )}
        </div>
        <div style={{ flex: 1 }}>
          <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp"
                 onChange={onPick} style={{ display: "none" }} data-testid={`${testid}-file-input`}/>
          <button type="button" onClick={() => inputRef.current?.click()}
                  className="admin-btn admin-btn-ghost" disabled={uploading}
                  data-testid={`${testid}-upload-btn`}>
            {uploading ? "Lade hoch…" : "Bild ändern"}
          </button>
          {err && <p className="admin-error" style={{ marginTop: "0.4rem" }}>{err}</p>}
          <p className="admin-align-hint" style={{ margin: "0.4rem 0 0", fontSize: "0.78rem" }}>
            JPG, PNG oder WebP, max. 8 MB. Wird auf 1600 px verkleinert.
          </p>
        </div>
      </div>
    </div>
  );
}

/* -------- List Editor (generisch, add/remove/reorder mit Hoch/Runter) -------- */
function ListEditor({ items = [], onChange, fields, addLabel, testidBase }) {
  const addItem = () => {
    const empty = fields.reduce((acc, f) => ({ ...acc, [f.key]: "" }), {});
    onChange([...items, empty]);
  };
  const updateItem = (idx, key, val) => {
    const next = [...items];
    next[idx] = { ...next[idx], [key]: val };
    onChange(next);
  };
  const removeItem = (idx) => onChange(items.filter((_, i) => i !== idx));
  const moveItem = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };

  return (
    <div data-testid={`${testidBase}-list`}>
      {items.length === 0 && (
        <p className="admin-empty" style={{ margin: "0.5rem 0" }}>Keine Einträge.</p>
      )}
      {items.map((item, idx) => (
        <div key={idx} className="list-editor-row" data-testid={`${testidBase}-item-${idx}`}>
          <div className="list-editor-fields">
            {fields.map((f) => (
              <label key={f.key} className="admin-label" style={{ margin: 0 }}>
                <span style={{ fontSize: "0.78rem", color: "#8B8580" }}>{f.label}</span>
                {f.multiline ? (
                  <textarea rows={2} value={item[f.key] || ""} placeholder={f.placeholder}
                            onChange={(e) => updateItem(idx, f.key, e.target.value)}
                            data-testid={`${testidBase}-${f.key}-${idx}`}/>
                ) : (
                  <input type="text" value={item[f.key] || ""} placeholder={f.placeholder}
                         onChange={(e) => updateItem(idx, f.key, e.target.value)}
                         data-testid={`${testidBase}-${f.key}-${idx}`}/>
                )}
              </label>
            ))}
          </div>
          <div className="list-editor-actions">
            <button type="button" onClick={() => moveItem(idx, -1)} disabled={idx === 0}
                    className="admin-btn admin-btn-ghost" title="hoch"
                    data-testid={`${testidBase}-up-${idx}`}>↑</button>
            <button type="button" onClick={() => moveItem(idx, 1)} disabled={idx === items.length - 1}
                    className="admin-btn admin-btn-ghost" title="runter"
                    data-testid={`${testidBase}-down-${idx}`}>↓</button>
            <button type="button" onClick={() => removeItem(idx)}
                    className="admin-btn admin-btn-danger" title="löschen"
                    data-testid={`${testidBase}-remove-${idx}`}>×</button>
          </div>
        </div>
      ))}
      <button type="button" onClick={addItem} className="admin-btn admin-btn-ghost"
              style={{ marginTop: "0.6rem" }} data-testid={`${testidBase}-add`}>
        {addLabel}
      </button>
    </div>
  );
}

/* -------- Tag List Editor (einfache String-Liste) -------- */
function TagListEditor({ items = [], onChange, addPlaceholder, testidBase }) {
  const [newTag, setNewTag] = useState("");
  const addTag = () => {
    const t = newTag.trim();
    if (!t) return;
    onChange([...items, t]);
    setNewTag("");
  };
  const removeTag = (idx) => onChange(items.filter((_, i) => i !== idx));
  const moveTag = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };
  const editTag = (idx, val) => {
    const next = [...items];
    next[idx] = val;
    onChange(next);
  };

  return (
    <div data-testid={`${testidBase}-list`}>
      <ul style={{ listStyle: "none", padding: 0, margin: "0.5rem 0",
                   display: "flex", flexDirection: "column", gap: "0.4rem" }}>
        {items.map((tag, idx) => (
          <li key={idx} style={{ display: "grid", gridTemplateColumns: "1fr auto auto auto",
                                 gap: "0.3rem", alignItems: "center" }}
              data-testid={`${testidBase}-item-${idx}`}>
            <input type="text" value={tag}
                   onChange={(e) => editTag(idx, e.target.value)}
                   style={{ padding: "0.55rem 0.75rem", border: "1px solid #DFD3B8",
                            borderRadius: "8px", background: "#FBF8F3", fontSize: "0.92rem" }}
                   data-testid={`${testidBase}-input-${idx}`}/>
            <button type="button" onClick={() => moveTag(idx, -1)} className="admin-btn admin-btn-ghost"
                    disabled={idx === 0} title="hoch">↑</button>
            <button type="button" onClick={() => moveTag(idx, 1)} className="admin-btn admin-btn-ghost"
                    disabled={idx === items.length - 1} title="runter">↓</button>
            <button type="button" onClick={() => removeTag(idx)} className="admin-btn admin-btn-danger"
                    title="entfernen">×</button>
          </li>
        ))}
      </ul>
      <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "0.5rem" }}>
        <input type="text" value={newTag} onChange={(e) => setNewTag(e.target.value)}
               onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
               placeholder={addPlaceholder}
               style={{ padding: "0.6rem 0.8rem", border: "1px solid #DFD3B8",
                        borderRadius: "10px", background: "#FBF8F3" }}
               data-testid={`${testidBase}-new-input`}/>
        <button type="button" onClick={addTag} className="admin-btn admin-btn-primary"
                data-testid={`${testidBase}-add`}>+ Hinzufügen</button>
      </div>
    </div>
  );
}
